import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

const TicketEditForm = ({ ticket, onInputChange, onSubmit }) => {
  const [formTicket, setFormTicket] = useState(ticket); // Ticket form state
  const [image, setImage] = useState(null); // State for optional image
  const [isSubmitting, setIsSubmitting] = useState(false); // Track if the form is submitting
  const [errorMessage, setErrorMessage] = useState(""); // Store error messages
  const [successMessage, setSuccessMessage] = useState(""); // Store success messages

  // Update formTicket state when ticket props change
  useEffect(() => {
    setFormTicket(ticket);
  }, [ticket]);

  // Handle input field changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const updatedTicket = { ...formTicket, [name]: value };
    setFormTicket(updatedTicket);
    onInputChange(e); // Notify parent about changes
  };

  // Handle file input changes
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImage(file); // Set the uploaded image file
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    toast.info("Updating ticket...");
    e.preventDefault();
    setIsSubmitting(true); // Disable the submit button
    setErrorMessage(""); // Reset error message
    setSuccessMessage(""); // Reset success message

    // Create FormData and append the ticket object and image
    const formData = new FormData();

    // Add formTicket data as JSON
    const ticketData = { ...formTicket };
    if (image) {
      ticketData.image_file = image; // Set image directly on ticketData
    }

    formData.append("ticket", JSON.stringify(ticketData)); // Append the updated ticket data (stringified)

    // Append the image file (if available)
    if (image) {
      formData.append("image", image);
    }

    try {
      // Call the parent onSubmit handler with the form data
      await onSubmit(formData);
      toast.success("Ticket updated successfully!");

      // Show success message
      setSuccessMessage("Ticket updated successfully!");
    } catch (error) {
      toast.error("Failed to update ticket. Please try again.");
      // Show error message
      setErrorMessage("An error occurred while updating the ticket.");
    } finally {
      // Re-enable the submit button after the submission process is complete
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mt-4 scroll">
      <form onSubmit={handleFormSubmit}>

        <div className="row">
          {/* Ticket ID */}
          <div className="mb-3 col-md-4">
            <input
              type="text"
              className="form-control readonly-input"
              // value={formTicket.ticket_id}
              value={`ID: ${formTicket.ticket_id}`}
              readOnly
            />
            {/* <small className="fs-7 text-center">Ticket ID:</small> */}
          </div>

          {/* Technician */}
          <div className="mb-3 col-md-4">
            <input
              type="text"
              className="form-control readonly-input"
              name="technician_name"
              value={`Tech: ${formTicket.technician_name}`}
              readOnly
            />
          </div>

          {/* Priority */}
          <div className="mb-3 col-md-4">
            <input
              type="text"
              className="form-control readonly-input"
              name="ticket_priority"
              value={`Priority: ${formTicket.ticket_priority}`}
              readOnly
            />
          </div>


          {/* Client Name */}
          <div className="mb-3 col-md-6">
            <label className="form-label"><strong>Client Name</strong></label>
            <input
              type="text"
              className="form-control readonly-input"
              name="client_name"
              value={formTicket.client_name}
              onChange={handleFormChange}
            />
          </div>

          {/* Client Phone */}
          <div className="mb-3 col-md-6">
            <label className="form-label"><strong>Client Phone</strong></label>
            <input
              type="text"
              className="form-control readonly-input"
              name="client_phone"
              value={formTicket.client_phone}
              onChange={handleFormChange}
            />
          </div>
          {/* Location */}
          <div className="mb-3 col-md-6">
            <label className="form-label"><strong>Location</strong></label>
            <input
              type="text"
              className="form-control"
              name="location"
              value={formTicket.location}
              onChange={handleFormChange}
            />
          </div>

          {/* Status */}
          <div className="mb-3 col-md-6">
            <label className="form-label"><strong>Status</strong></label>
            <select
              className="form-select"
              name="ticket_status"
              value={formTicket.ticket_status}
              onChange={handleFormChange}
            >
              <option value="Pending">Pending</option>
              <option value="Ongoing">Ongoing</option>
              <option value="Postpone">Postponed</option>
              <option value="Complete">Completed</option>
            </select>
          </div>
          <hr />


          {/* Feedback */}
          <div className="mb-4 col-12 col-md-8 mx-auto">
            <label className="form-label fw-semibold text-primary fs-6">
              Feedback
            </label>
            <textarea
              className="form-control rounded-3 shadow-sm"
              name="ticket_feedback"
              value={formTicket.ticket_feedback || ""}
              onChange={handleFormChange}
              rows="4"
              placeholder="Enter feedback or additional notes here..."
              style={{ resize: 'vertical' }}
            ></textarea>
          </div>



        </div>






        {/* Date Raised */}
        <div className="mb-3">
          <label className="form-label"><strong>Date Raised</strong></label>
          <input
            type="datetime-local"
            className="form-control"
            name="expiry_date"
            value={
              formTicket.expiry_date
                ? new Date(formTicket.expiry_date).toISOString().slice(0, -1)
                : ""
            }
            onChange={handleFormChange}
          />
        </div>

        {/* Conditional Image Input */}
        {formTicket.ticket_status !== "Pending" && (
          <div className="mb-3">
            <label className="form-label"><strong>Upload Image</strong></label>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleFileChange}
            />
          </div>
        )}

        {/* Image Preview */}
        {(image || formTicket.image_file) && (
          <div className="mb-3">
            <label className="form-label"><strong>Image Preview</strong></label>
            <img
              src={formTicket.image_file ? `https://res.cloudinary.com/gtckenya/${formTicket.image_file}` : ""}
              alt="Preview"
              width={300}
            />
          </div>
        )}

        {/* Progress/Error Message */}
        {isSubmitting && <p className="text-muted">Submitting...</p>}
        {errorMessage && <p className="text-danger">{errorMessage}</p>}
        {successMessage && <p className="text-success">{successMessage}</p>}

        {/* Buttons */}
        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onSubmit(null)}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting} // Disable button while submitting
          >
            {isSubmitting ? "Updating..." : "Update Ticket"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TicketEditForm;
